import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adoptPoolCreator,
  getExploreAccess,
  getPoolCreator,
  joinCreatorPool,
  leaveCreatorPool,
  searchPoolCreators,
} from '../lib/exploreApi';
import type { ExploreSearchParams } from '../types/explore';

export const exploreKeys = {
  all: ['explore'] as const,
  access: ['explore', 'access'] as const,
  search: (params: ExploreSearchParams) => ['explore', 'search', params] as const,
  creator: (id: string) => ['explore', 'creator', id] as const,
};

export function useExploreAccess() {
  return useQuery({
    queryKey: exploreKeys.access,
    queryFn: getExploreAccess,
    staleTime: 60_000,
  });
}

/**
 * Infinite search over the pool.
 *
 * `enabled` is load-bearing rather than cosmetic: every page costs the agency read quota, so the
 * query must not fire until we know they are allowed to read at all. Firing first and handling the
 * 403 would spend nothing, but it would also flash an error at someone who simply has not joined
 * yet — the opt-in gate is the right thing to show them.
 */
export function useExploreSearch(params: ExploreSearchParams, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: exploreKeys.search(params),
    queryFn: ({ pageParam }) =>
      searchPoolCreators({ ...params, cursor: pageParam as string | undefined }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
    // Long, because a repeat of the same search should not cost quota again.
    staleTime: 5 * 60 * 1000,
  });
}

export function usePoolCreator(id: string | null) {
  return useQuery({
    queryKey: exploreKeys.creator(id ?? ''),
    queryFn: () => getPoolCreator(id!),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
  });
}

export function useJoinCreatorPool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: joinCreatorPool,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: exploreKeys.all }),
  });
}

export function useLeaveCreatorPool() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: leaveCreatorPool,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: exploreKeys.all }),
  });
}

export function useAdoptCreator() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adoptPoolCreator,
    onSuccess: () => {
      // The search results carry `inRoster`, so they are stale the moment an adoption succeeds.
      void queryClient.invalidateQueries({ queryKey: exploreKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['kols'] });
    },
  });
}
